import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { ibkrService } from '../../services/ibkrService';

interface Settings {
  host: string;
  port: number;
  client_id: number;
  mode: string;
  symbols: string[];
  max_risk_pct: number;
  max_positions: number;
  profit_target_pct: number;
  stop_loss_multiplier: number;
  analysis_interval_min: number;
}

const AVAILABLE_SYMBOLS = [
  { key: 'SPY', label: 'SPY (S&P 500 ETF)', group: 'Aktien/ETFs' },
  { key: 'QQQ', label: 'QQQ (Nasdaq 100 ETF)', group: 'Aktien/ETFs' },
  { key: 'IWM', label: 'IWM (Russell 2000 ETF)', group: 'Aktien/ETFs' },
  { key: 'ES', label: 'ES (E-Mini S&P 500 Futures)', group: 'Futures' },
  { key: 'MES', label: 'MES (Micro E-Mini S&P 500)', group: 'Futures' },
  { key: 'NQ', label: 'NQ (E-Mini Nasdaq Futures)', group: 'Futures' },
  { key: 'MNQ', label: 'MNQ (Micro E-Mini Nasdaq)', group: 'Futures' },
];

const DEFAULT_SETTINGS: Settings = {
  host: 'bitvavo_ibkr_gateway',
  port: 4004,
  client_id: 1,
  mode: 'auto',
  symbols: ['SPY', 'QQQ', 'ES', 'MES'],
  max_risk_pct: 5,
  max_positions: 3,
  profit_target_pct: 50,
  stop_loss_multiplier: 2.0,
  analysis_interval_min: 60,
};

const IBKRSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setError(null);
      const data = await ibkrService.getSettings();
      // BUG-IBKR-24: Backend speichert max_risk_pct als Dezimal (0.05),
      // Formular zeigt Prozent (5). Beim Laden konvertieren.
      setSettings({
        ...DEFAULT_SETTINGS,
        ...data,
        max_risk_pct: (data.max_risk_pct ?? 0.05) * 100,
      });
    } catch (err: any) {
      // Use defaults if settings not found
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field: keyof Settings, value: any) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
  };

  const handleSymbolToggle = (symbol: string) => {
    setSettings((prev) => ({
      ...prev,
      symbols: prev.symbols.includes(symbol)
        ? prev.symbols.filter((s) => s !== symbol)
        : [...prev.symbols, symbol],
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // BUG-IBKR-24: Zurueck zu Dezimal fuer Backend (5 → 0.05)
      await ibkrService.updateSettings({
        ...settings,
        max_risk_pct: settings.max_risk_pct / 100,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        IBKR Einstellungen
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Einstellungen gespeichert
        </Alert>
      )}

      {/* Connection */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Verbindung
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Host"
            value={settings.host}
            onChange={(e) => handleChange('host', e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
          <TextField
            label="Port"
            type="number"
            value={settings.port}
            onChange={(e) => handleChange('port', parseInt(e.target.value) || 0)}
            size="small"
            sx={{ width: 120 }}
          />
          <TextField
            label="Client ID"
            type="number"
            value={settings.client_id}
            onChange={(e) => handleChange('client_id', parseInt(e.target.value) || 0)}
            size="small"
            sx={{ width: 120 }}
          />
        </Box>
      </Paper>

      {/* Trading Mode */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Trading-Modus
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Modus</InputLabel>
          <Select
            value={settings.mode}
            label="Modus"
            onChange={(e) => handleChange('mode', e.target.value)}
          >
            <MenuItem value="auto">Voll-Auto</MenuItem>
            <MenuItem value="manual">Manuell</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Underlyings */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Underlyings
        </Typography>
        {['Aktien/ETFs', 'Futures'].map((group) => (
          <Box key={group} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{group}</Typography>
            <FormGroup row>
              {AVAILABLE_SYMBOLS.filter((s) => s.group === group).map((s) => (
                <FormControlLabel
                  key={s.key}
                  control={
                    <Checkbox
                      checked={settings.symbols.includes(s.key)}
                      onChange={() => handleSymbolToggle(s.key)}
                    />
                  }
                  label={s.label}
                />
              ))}
            </FormGroup>
          </Box>
        ))}
      </Paper>

      {/* Risk Parameters */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Risiko
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Max Risiko pro Trade (%)"
            type="number"
            value={settings.max_risk_pct}
            onChange={(e) => handleChange('max_risk_pct', parseFloat(e.target.value) || 0)}
            size="small"
            sx={{ width: 200 }}
            inputProps={{ step: 0.5, min: 0.5, max: 20 }}
          />
          <TextField
            label="Max offene Positionen"
            type="number"
            value={settings.max_positions}
            onChange={(e) => handleChange('max_positions', parseInt(e.target.value) || 1)}
            size="small"
            sx={{ width: 200 }}
            inputProps={{ min: 1, max: 10 }}
          />
          <TextField
            label="Profit-Target (%)"
            type="number"
            value={settings.profit_target_pct}
            onChange={(e) => handleChange('profit_target_pct', parseFloat(e.target.value) || 0)}
            size="small"
            sx={{ width: 200 }}
            inputProps={{ step: 5, min: 10, max: 100 }}
          />
          <TextField
            label="Stop-Loss Multiplier (x)"
            type="number"
            value={settings.stop_loss_multiplier}
            onChange={(e) => handleChange('stop_loss_multiplier', parseFloat(e.target.value) || 0)}
            size="small"
            sx={{ width: 200 }}
            inputProps={{ step: 0.5, min: 0.5, max: 5 }}
          />
        </Box>
      </Paper>

      {/* Analysis Interval */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Analyse-Intervall
        </Typography>
        <TextField
          label="Alle X Minuten neue Analyse"
          type="number"
          value={settings.analysis_interval_min}
          onChange={(e) => handleChange('analysis_interval_min', parseInt(e.target.value) || 15)}
          size="small"
          sx={{ width: 200 }}
          inputProps={{ min: 5, max: 240, step: 5 }}
        />
      </Paper>

      {/* Save Button */}
      <Button
        variant="contained"
        size="large"
        startIcon={<SaveIcon />}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Speichere...' : 'Speichern'}
      </Button>
    </Box>
  );
};

export default IBKRSettings;
